
const pill = (label: string, color: string) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${color}`}>
    {label}
  </span>
);

const TH = ({ children }: { children: React.ReactNode }) => (
  <th className="px-1.5 py-1.5 sm:px-3 sm:py-2 text-left text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-slate-400">{children}</th>
);
const TD = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <td className={`px-1.5 py-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-[11px] text-slate-700 ${className}`}>{children}</td>
);

/* ── Header bar shared across previews ─────────────────────── */
function PreviewHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <div>
        <div className="text-xs font-bold text-[#0a2d4a]">{title}</div>
        {subtitle && <div className="text-[10px] text-slate-400">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-1.5">
        <div className="h-2 w-2 rounded-full bg-green-400" />
        <span className="text-[10px] text-slate-400">Live</span>
      </div>
    </div>
  );
}

/* ── Stat card ─────────────────────────────────────────────── */
function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div className={`rounded-lg border p-2 sm:p-2.5 ${color}`}>
      <div className="text-[10px] text-slate-500 truncate" title={label}>{label}</div>
      <div className="mt-0.5 text-sm sm:text-base font-extrabold text-[#0a2d4a]">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 truncate">{sub}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 1. HRMS                                                      */
/* ─────────────────────────────────────────────────────────── */
export function HRMSPreview() {
  const rows = [
    { name: "Rajesh Sharma", dept: "Engineering", designation: "CEO", status: "Active" },
    { name: "Priya Nair", dept: "Human Resources", designation: "HR Head", status: "Active" },
    { name: "Amit Agarwal", dept: "Finance", designation: "Finance Head", status: "Active" },
    { name: "Vikram Reddy", dept: "Sales", designation: "Sales Head", status: "Active" },
    { name: "Neha Gupta", dept: "Operations", designation: "Ops Manager", status: "On Leave" },
  ];
  const initials = (n: string) => n.split(" ").map(w => w[0]).join("");
  const colors = ["from-violet-500 to-purple-700", "from-blue-500 to-blue-700", "from-emerald-500 to-green-700", "from-orange-500 to-amber-700", "from-rose-500 to-red-700"];
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Employee Directory</span>
        <span className="text-[10px] text-blue-300">248 employees</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {["All", "Engineering", "HR", "Finance", "Sales"].map((t, i) => (
            <span key={t} className={`rounded-full px-2 py-0.5 text-[9px] sm:text-[10px] font-semibold ${i === 0 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>{t}</span>
          ))}
        </div>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[320px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Employee</TH><TH>Department</TH><TH>Designation</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.name} className="border-b border-slate-50 last:border-0">
                  <TD>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${colors[i]} text-[8px] font-bold text-white`}>{initials(r.name)}</div>
                      <span className="font-medium text-[#0a2d4a]">{r.name}</span>
                    </div>
                  </TD>
                  <TD>{r.dept}</TD>
                  <TD>{r.designation}</TD>
                  <TD>{pill(r.status, r.status === "Active" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700")}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 2. Payroll                                                    */
/* ─────────────────────────────────────────────────────────── */
export function PayrollPreview() {
  const rows = [
    { name: "Rajesh Sharma", basic: "₹1,20,000", hra: "₹48,000", gross: "₹1,80,500", net: "₹1,54,320" },
    { name: "Priya Nair", basic: "₹85,000", hra: "₹34,000", gross: "₹1,23,500", net: "₹1,07,890" },
    { name: "Amit Agarwal", basic: "₹90,000", hra: "₹36,000", gross: "₹1,30,200", net: "₹1,12,450" },
    { name: "Vikram Reddy", basic: "₹75,000", hra: "₹30,000", gross: "₹1,05,000", net: "₹91,340" },
  ];
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Payroll Run — May 2025</span>
        {pill("Processing", "bg-amber-100 text-amber-700")}
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Total Employees" value="248" color="bg-blue-50 border-blue-100" />
          <StatCard label="Gross Payroll" value="₹2.4 Cr" color="bg-green-50 border-green-100" />
          <StatCard label="Net Payroll" value="₹2.1 Cr" color="bg-violet-50 border-violet-100" />
        </div>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Employee</TH><TH>Basic</TH><TH>HRA</TH><TH>Gross</TH><TH>Net Pay</TH>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.name} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-medium text-[#0a2d4a]">{r.name}</span></TD>
                  <TD>{r.basic}</TD><TD>{r.hra}</TD><TD>{r.gross}</TD>
                  <TD><span className="font-semibold text-green-700">{r.net}</span></TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 3. Finance                                                    */
/* ─────────────────────────────────────────────────────────── */
export function FinancePreview() {
  const invoices = [
    { no: "INV-2025-0341", party: "Hexacore Solutions", amount: "₹4,82,000", gst: "₹86,760", status: "Paid" },
    { no: "INV-2025-0342", party: "TechBridge Pvt Ltd", amount: "₹2,15,500", gst: "₹38,790", status: "Pending" },
    { no: "INV-2025-0343", party: "Meraki Systems", amount: "₹6,00,000", gst: "₹1,08,000", status: "Overdue" },
    { no: "INV-2025-0344", party: "Cloudnine Infra", amount: "₹1,37,200", gst: "₹24,696", status: "Paid" },
  ];
  const statusColor: Record<string, string> = { Paid: "bg-green-50 text-green-700", Pending: "bg-amber-50 text-amber-700", Overdue: "bg-red-50 text-red-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Finance — FY 2025–26</span>
        <span className="text-[10px] text-blue-300">Alea Technologies Pvt Ltd</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Total Revenue" value="₹8.4 Cr" sub="+12% vs LY" color="bg-green-50 border-green-100" />
          <StatCard label="Outstanding" value="₹91.2 L" sub="23 invoices" color="bg-amber-50 border-amber-100" />
          <StatCard label="GST Payable" value="₹14.7 L" sub="GSTR-3B due" color="bg-blue-50 border-blue-100" />
        </div>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Invoice</TH><TH>Party</TH><TH>Amount</TH><TH>GST</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {invoices.map(r => (
                <tr key={r.no} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-mono text-blue-600">{r.no}</span></TD>
                  <TD>{r.party}</TD>
                  <TD><span className="font-semibold">{r.amount}</span></TD>
                  <TD className="text-slate-500">{r.gst}</TD>
                  <TD>{pill(r.status, statusColor[r.status])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 4. Procurement                                               */
/* ─────────────────────────────────────────────────────────── */
export function ProcurementPreview() {
  const pos = [
    { no: "PO-2025-0089", vendor: "TechBridge Pvt Ltd", items: 4, value: "₹3,42,000", status: "Approved" },
    { no: "PO-2025-0090", vendor: "Meraki Systems", items: 2, value: "₹1,18,500", status: "Pending" },
    { no: "PO-2025-0091", vendor: "Cloudnine Infra", items: 7, value: "₹5,06,200", status: "Approved" },
    { no: "PO-2025-0092", vendor: "Hexacore Solutions", items: 1, value: "₹82,000", status: "Draft" },
    { no: "PO-2025-0093", vendor: "BlueSky Networks", items: 3, value: "₹2,25,750", status: "Pending" },
  ];
  const statusColor: Record<string, string> = { Approved: "bg-green-50 text-green-700", Pending: "bg-amber-50 text-amber-700", Draft: "bg-slate-100 text-slate-500" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Purchase Orders</span>
        <span className="text-[10px] text-blue-300">Q2 FY 2025–26</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Open POs" value="34" color="bg-blue-50 border-blue-100" />
          <StatCard label="Spend (Q2)" value="₹1.2 Cr" color="bg-green-50 border-green-100" />
          <StatCard label="Pending Approval" value="8" color="bg-amber-50 border-amber-100" />
        </div>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>PO Number</TH><TH>Vendor</TH><TH>Items</TH><TH>Value</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {pos.map(r => (
                <tr key={r.no} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-mono text-blue-600">{r.no}</span></TD>
                  <TD>{r.vendor}</TD>
                  <TD className="text-center">{r.items}</TD>
                  <TD><span className="font-semibold">{r.value}</span></TD>
                  <TD>{pill(r.status, statusColor[r.status])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 5. Vendor Portal                                             */
/* ─────────────────────────────────────────────────────────── */
export function VendorPortalPreview() {
  const items = [
    { no: "PO-2025-0089", date: "12 Apr 2025", value: "₹3,42,000", due: "26 Apr 2025", status: "Payment Sent" },
    { no: "PO-2025-0091", date: "19 Apr 2025", value: "₹5,06,200", due: "03 May 2025", status: "Confirmed" },
    { no: "PO-2025-0093", date: "28 Apr 2025", value: "₹2,25,750", due: "12 May 2025", status: "Under Review" },
  ];
  const statusColor: Record<string, string> = { "Payment Sent": "bg-green-50 text-green-700", Confirmed: "bg-blue-50 text-blue-700", "Under Review": "bg-amber-50 text-amber-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold text-white">TechBridge Pvt Ltd</div>
          <div className="text-[10px] text-blue-300">Vendor Portal</div>
        </div>
        {pill("Approved Vendor", "bg-green-900/40 text-green-300")}
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Active POs" value="3" color="bg-blue-50 border-blue-100" />
          <StatCard label="Total Value" value="₹10.7 L" color="bg-green-50 border-green-100" />
          <StatCard label="Payments Due" value="₹2.3 L" color="bg-amber-50 border-amber-100" />
        </div>
        <PreviewHeader title="Purchase Orders" subtitle="Track your POs and payments in real time" />
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>PO No.</TH><TH>Date</TH><TH>Value</TH><TH>Due Date</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {items.map(r => (
                <tr key={r.no} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-mono text-blue-600">{r.no}</span></TD>
                  <TD>{r.date}</TD>
                  <TD><span className="font-semibold">{r.value}</span></TD>
                  <TD className="text-slate-500">{r.due}</TD>
                  <TD>{pill(r.status, statusColor[r.status])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 6. Employee Self-Service                                     */
/* ─────────────────────────────────────────────────────────── */
export function SelfServicePreview() {
  const leaves = [
    { type: "Casual Leave", applied: "02 May 2025", days: 2, status: "Approved" },
    { type: "Sick Leave", applied: "14 May 2025", days: 1, status: "Approved" },
    { type: "Annual Leave", applied: "20 May 2025", days: 5, status: "Pending" },
  ];
  const statusColor: Record<string, string> = { Approved: "bg-green-50 text-green-700", Pending: "bg-amber-50 text-amber-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center gap-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-sky-700 text-[10px] font-bold text-white">AV</div>
        <div>
          <div className="text-xs font-bold text-white">Anjali Verma</div>
          <div className="text-[10px] text-blue-300">My Portal · Employee</div>
        </div>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Days Present" value="21" sub="May 2025" color="bg-green-50 border-green-100" />
          <StatCard label="Leave Balance" value="12" sub="days left" color="bg-blue-50 border-blue-100" />
          <StatCard label="CL Taken" value="3" sub="of 12" color="bg-violet-50 border-violet-100" />
          <StatCard label="Payslips" value="5" sub="available" color="bg-amber-50 border-amber-100" />
        </div>
        <PreviewHeader title="Leave Requests" />
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[320px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Type</TH><TH>Applied</TH><TH>Days</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {leaves.map(r => (
                <tr key={r.type + r.applied} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-medium text-[#0a2d4a]">{r.type}</span></TD>
                  <TD>{r.applied}</TD>
                  <TD className="text-center">{r.days}</TD>
                  <TD>{pill(r.status, statusColor[r.status])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 7. Analytics                                                 */
/* ─────────────────────────────────────────────────────────── */
export function AnalyticsPreview() {
  const bars = [
    { label: "Jan", h: 55 }, { label: "Feb", h: 70 }, { label: "Mar", h: 62 },
    { label: "Apr", h: 80 }, { label: "May", h: 90 }, { label: "Jun", h: 75 },
  ];
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Analytics Dashboard</span>
        <span className="text-[10px] text-blue-300">FY 2025–26</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Revenue" value="₹8.4 Cr" sub="+12% YoY" color="bg-green-50 border-green-100" />
          <StatCard label="Headcount" value="248" sub="+18 this Q" color="bg-blue-50 border-blue-100" />
          <StatCard label="Open POs" value="34" sub="₹1.2 Cr" color="bg-amber-50 border-amber-100" />
          <StatCard label="Payroll" value="₹2.4 Cr" sub="May 2025" color="bg-violet-50 border-violet-100" />
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-2.5 sm:p-3">
          <div className="mb-2 text-[10px] font-semibold text-slate-500">Monthly Revenue (₹ Lakhs)</div>
          <div className="flex items-end justify-between gap-1.5 h-20">
            {bars.map((b) => (
              <div key={b.label} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-blue-600 to-blue-400"
                  style={{ height: `${b.h}%` }}
                />
                <span className="text-[9px] text-slate-400">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 8. RBAC                                                      */
/* ─────────────────────────────────────────────────────────── */
export function RBACPreview() {
  const roles = [
    { role: "Super Admin", hr: true, payroll: true, finance: true, procurement: true, users: 2 },
    { role: "HR Head", hr: true, payroll: true, finance: false, procurement: false, users: 1 },
    { role: "Finance Head", hr: false, payroll: true, finance: true, procurement: true, users: 1 },
    { role: "Sales Head", hr: false, payroll: false, finance: false, procurement: false, users: 3 },
    { role: "Employee", hr: false, payroll: false, finance: false, procurement: false, users: 241 },
  ];
  const Tick = ({ v }: { v: boolean }) => (
    <td className="px-1.5 py-1.5 sm:px-3 sm:py-2 text-center text-[10px] sm:text-[11px]">
      {v ? <span className="text-green-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}
    </td>
  );
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Roles & Permissions</span>
        <span className="text-[10px] text-blue-300">5 roles configured</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Role</TH><TH>HR</TH><TH>Payroll</TH><TH>Finance</TH><TH>Procurement</TH><TH>Users</TH>
            </tr></thead>
            <tbody>
              {roles.map(r => (
                <tr key={r.role} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-medium text-[#0a2d4a]">{r.role}</span></TD>
                  <Tick v={r.hr} /><Tick v={r.payroll} /><Tick v={r.finance} /><Tick v={r.procurement} />
                  <td className="px-1.5 py-1.5 sm:px-3 sm:py-2 text-center">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{r.users}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 9. Approval Engine                                           */
/* ─────────────────────────────────────────────────────────── */
export function ApprovalEnginePreview() {
  const queue = [
    { ref: "LV-2025-0342", type: "Leave Request", from: "Anjali Verma", submitted: "20 May 2025", status: "Pending" },
    { ref: "PO-2025-0090", type: "Purchase Order", from: "Neha Gupta", submitted: "19 May 2025", status: "Pending" },
    { ref: "EXP-2025-0118", type: "Expense Claim", from: "Vikram Reddy", submitted: "18 May 2025", status: "Approved" },
    { ref: "LV-2025-0339", type: "Leave Request", from: "Rohan Desai", submitted: "17 May 2025", status: "Rejected" },
    { ref: "PO-2025-0088", type: "Purchase Order", from: "Priya Nair", submitted: "16 May 2025", status: "Approved" },
  ];
  const statusColor: Record<string, string> = { Pending: "bg-amber-50 text-amber-700", Approved: "bg-green-50 text-green-700", Rejected: "bg-red-50 text-red-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Approval Queue</span>
        <span className="text-[10px] text-amber-300">2 pending your action</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Ref</TH><TH>Type</TH><TH>Requested By</TH><TH>Submitted</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {queue.map(r => (
                <tr key={r.ref} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-mono text-blue-600">{r.ref}</span></TD>
                  <TD>{r.type}</TD>
                  <TD>{r.from}</TD>
                  <TD className="text-slate-500">{r.submitted}</TD>
                  <TD>{pill(r.status, statusColor[r.status])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 10. Audit Logs                                               */
/* ─────────────────────────────────────────────────────────── */
export function AuditLogsPreview() {
  const logs = [
    { actor: "Rajesh Sharma", action: "Approved payroll run", module: "Payroll", time: "Today 11:42 AM", level: "Info" },
    { actor: "Priya Nair", action: "Updated employee record", module: "HR", time: "Today 10:18 AM", level: "Info" },
    { actor: "Amit Agarwal", action: "Posted journal entry", module: "Finance", time: "Today 09:55 AM", level: "Info" },
    { actor: "Neha Gupta", action: "Created PO PO-2025-0093", module: "Procurement", time: "Yesterday", level: "Info" },
    { actor: "System", action: "Failed login attempt", module: "Auth", time: "Yesterday", level: "Warn" },
  ];
  const levelColor: Record<string, string> = { Info: "bg-blue-50 text-blue-700", Warn: "bg-amber-50 text-amber-700", Error: "bg-red-50 text-red-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Audit Trail</span>
        <span className="text-[10px] text-blue-300">Last 24 hours · 847 events</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Actor</TH><TH>Action</TH><TH>Module</TH><TH>Time</TH><TH>Level</TH>
            </tr></thead>
            <tbody>
              {logs.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-medium text-[#0a2d4a]">{r.actor}</span></TD>
                  <TD>{r.action}</TD>
                  <TD>{pill(r.module, "bg-slate-100 text-slate-600")}</TD>
                  <TD className="text-slate-400">{r.time}</TD>
                  <TD>{pill(r.level, levelColor[r.level])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 11. Reports                                                  */
/* ─────────────────────────────────────────────────────────── */
export function ReportsPreview() {
  const reports = [
    { name: "Payroll Summary — May 2025", category: "Payroll", generated: "31 May 2025", format: "XLSX" },
    { name: "GSTR-1 Return Q1 FY26", category: "Finance", generated: "30 Apr 2025", format: "JSON" },
    { name: "Attendance Register — Apr 2025", category: "HR", generated: "01 May 2025", format: "PDF" },
    { name: "PF ECR Challan — May 2025", category: "Statutory", generated: "31 May 2025", format: "TXT" },
    { name: "Leave Balances — All Staff", category: "HR", generated: "29 May 2025", format: "XLSX" },
  ];
  const catColor: Record<string, string> = { Payroll: "bg-violet-50 text-violet-700", Finance: "bg-green-50 text-green-700", HR: "bg-blue-50 text-blue-700", Statutory: "bg-amber-50 text-amber-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">MIS Reports</span>
        <span className="text-[10px] text-blue-300">24 reports available</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Report Name</TH><TH>Category</TH><TH>Generated</TH><TH>Format</TH>
            </tr></thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.name} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-medium text-[#0a2d4a]">{r.name}</span></TD>
                  <TD>{pill(r.category, catColor[r.category])}</TD>
                  <TD className="text-slate-500">{r.generated}</TD>
                  <TD>
                    <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{r.format}</span>
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 12. Learning & Travel                                        */
/* ─────────────────────────────────────────────────────────── */
export function LearningTravelPreview() {
  const travel = [
    { ref: "TR-2025-041", emp: "Vikram Reddy", dest: "Mumbai", dates: "10–12 Jun", expense: "₹18,400", status: "Approved" },
    { ref: "TR-2025-042", emp: "Priya Nair", dest: "Bangalore", dates: "15–16 Jun", expense: "₹12,200", status: "Pending" },
    { ref: "LD-2025-018", emp: "Neha Gupta", dest: "AWS Certification", dates: "Online", expense: "₹8,500", status: "Approved" },
    { ref: "TR-2025-043", emp: "Amit Agarwal", dest: "Delhi", dates: "20–22 Jun", expense: "₹21,750", status: "Pending" },
  ];
  const statusColor: Record<string, string> = { Approved: "bg-green-50 text-green-700", Pending: "bg-amber-50 text-amber-700" };
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-[#0a2d4a] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-white">Travel & L&D Requests</span>
        <span className="text-[10px] text-blue-300">Jun 2025</span>
      </div>
      <div className="p-2.5 sm:p-3">
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Travel Requests" value="12" sub="this month" color="bg-blue-50 border-blue-100" />
          <StatCard label="L&D Enrolments" value="7" sub="active" color="bg-violet-50 border-violet-100" />
          <StatCard label="Pending Claims" value="₹60.9K" color="bg-amber-50 border-amber-100" />
        </div>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <table className="w-full min-w-[340px]">
            <thead><tr className="border-b border-slate-100">
              <TH>Ref</TH><TH>Employee</TH><TH>Destination</TH><TH>Dates</TH><TH>Amount</TH><TH>Status</TH>
            </tr></thead>
            <tbody>
              {travel.map(r => (
                <tr key={r.ref} className="border-b border-slate-50 last:border-0">
                  <TD><span className="font-mono text-blue-600">{r.ref}</span></TD>
                  <TD>{r.emp}</TD>
                  <TD>{r.dest}</TD>
                  <TD className="text-slate-500">{r.dates}</TD>
                  <TD><span className="font-semibold">{r.expense}</span></TD>
                  <TD>{pill(r.status, statusColor[r.status])}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
